/**
 * KYCCompliancePage — `/kyc/compliance`.
 *
 * Slice 2: KYC compliance officer dashboard.
 * - Shows audit trail from GET /v1/kyc/audit-trail (operator decisions).
 * - SAR queue from GET /v1/kyc/sars.
 * - Watchlist hits from GET /v1/kyc/watchlist/queue.
 * - Blocked customers KPI from GET /v1/kyc/analytics/summary (live count of
 *   amlFrozen=true KYC profiles).
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { Ban, CheckCircle, Eye, FileText, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getAnalyticsSummary, listSars, listWatchlistHits, getAuditTrail } from '../../lib/api/kyc';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function ErrorBanner({ title, message, testId }: { readonly title: string; readonly message: string; readonly testId?: string }): JSX.Element {
  return (
    <div
      className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1"
      data-testid={testId}
    >
      <p className="font-semibold">{title}</p>
      <p className="text-red-800/90">{message}</p>
    </div>
  );
}

export function KYCCompliancePage(): JSX.Element {
  const auditTrail = useQuery({
    queryKey: ['kyc', 'audit-trail'],
    queryFn: () => getAuditTrail({ pageSize: 25 }),
    retry: false,
    staleTime: 15_000,
  });

  const sars = useQuery({
    queryKey: ['kyc', 'sars', 'compliance'],
    queryFn: () => listSars({ pageSize: 10 }),
    retry: false,
  });

  const hits = useQuery({
    queryKey: ['kyc', 'hits', 'compliance'],
    queryFn: () => listWatchlistHits({ pageSize: 10 }),
    retry: false,
  });

  const summary = useQuery({
    queryKey: ['kyc', 'analytics', 'compliance-summary'],
    queryFn: () => getAnalyticsSummary(),
    retry: false,
    staleTime: 30_000,
  });

  const auditDenied =
    auditTrail.isError &&
    auditTrail.error instanceof ApiError &&
    (auditTrail.error.status === 401 || auditTrail.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Compliance dashboard"
        description="KYC operator audit trail, AML/CFT monitoring, SAR pipeline."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/kyc/sar"
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark inline-flex items-center gap-2"
            >
              <FileText className="w-4 h-4" /> File SAR
            </Link>
            <Link
              to="/kyc/watchlist"
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Watchlist queue
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Audit entries"
          value={auditTrail.isPending ? '—' : String(auditTrail.data?.total ?? 0)}
          hint="Approved + rejected by operators"
          icon={CheckCircle}
        />
        <KPICard
          title="Active watchlist hits"
          value={hits.isPending ? '—' : String(hits.data?.total ?? 0)}
          hint="Pending watchlist matches"
          icon={Eye}
        />
        <KPICard
          title="Pending SARs"
          value={sars.isPending ? '—' : String(sars.data?.items.filter((s) => s.status === 'DRAFT').length ?? 0)}
          hint="status=DRAFT"
          icon={FileText}
        />
        <KPICard
          title="Blocked customers"
          value={summary.isPending ? '—' : String(summary.data?.blockedCustomers ?? 0)}
          hint="amlFrozen=true profiles"
          icon={Ban}
          tone={summary.data && summary.data.blockedCustomers > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* KYC Operator Audit Trail */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Operator audit trail</h2>
        {auditDenied ? (
          <ErrorBanner
            title="Permission required"
            message="Token missing Kyc/readAuditTrail. Contact the platform admin to grant the role."
          />
        ) : auditTrail.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : auditTrail.isError ? (
          <ErrorBanner title="Audit trail unavailable" message={(auditTrail.error as Error).message} />
        ) : (auditTrail.data?.items.length ?? 0) === 0 ? (
          <div
            className="bg-white border border-border rounded-xl p-6 text-center"
            data-testid="empty-audit-trail"
          >
            <p className="text-sm text-muted">No operator decisions recorded yet.</p>
          </div>
        ) : (
          <div
            className="bg-white border border-border rounded-xl overflow-hidden"
            data-testid="audit-trail-table"
          >
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Action</th>
                  <th className="text-left px-4 py-2">Request ID</th>
                  <th className="text-left px-4 py-2">Citizen</th>
                  <th className="text-left px-4 py-2">Tier change</th>
                  <th className="text-left px-4 py-2">Operator</th>
                  <th className="text-left px-4 py-2">Decided at</th>
                  <th className="text-left px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {auditTrail.data?.items.map((entry) => (
                  <tr
                    key={entry.id}
                    className="hover:bg-gray-50"
                    data-testid={`audit-row-${entry.id}`}
                  >
                    <td className="px-4 py-2">
                      {entry.action === 'APPROVED' ? (
                        <span className="inline-flex items-center gap-1 text-success text-xs font-medium">
                          <CheckCircle className="w-3 h-3" /> Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-danger text-xs font-medium">
                          <XCircle className="w-3 h-3" /> Rejected
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">
                      {entry.id.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600">
                      {entry.citizenUserId.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700">
                      {entry.currentTier} → {entry.targetTier}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">
                      {entry.operatorId.slice(0, 8)}…
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(entry.actedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted max-w-[200px] truncate">
                      {entry.rejectionReason ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* SAR queue */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">SAR queue</h2>
        {sars.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : sars.isError ? (
          <ErrorBanner title="SAR queue unavailable" message={(sars.error as Error).message} />
        ) : (sars.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No SARs filed yet.
          </p>
        ) : (
          <div className="space-y-3">
            {sars.data?.items.map((sar) => (
              <div key={sar.id} className="bg-white rounded-xl border border-border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-900 font-mono text-xs">{sar.sarNumber}</span>
                    <StatusBadge status={sar.status} />
                  </div>
                  <span className="text-xs text-muted">
                    {sar.filedAt ? new Date(sar.filedAt).toLocaleDateString() : '—'}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-2">{sar.trigger}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
